import { Controller } from '@hotwired/stimulus';

// Toggles `.active` on the nav link whose href matches the section currently
// crossing under the sticky nav.
//
// Usage:
//   <nav data-controller="scrollspy-nav">
//     <a class="nav-link" href="#section1" data-action="click->scrollspy-nav#activateLink">Section 1</a>
//   </nav>
export default class extends Controller {
  connect() {
    this.links = Array.from(
      this.element.querySelectorAll('a[href^="#"]')
    );
    this.sections = this.links
      .map(link => document.getElementById(link.getAttribute('href').slice(1)))
      .filter(Boolean);

    if (this.sections.length === 0) return;

    this.intersectingIds = new Set();
    this.pendingActiveId = null;

    const navHeight = this.element.offsetHeight;
    this.observer = new IntersectionObserver(
      entries => this.updateActiveLink(entries),
      {
        rootMargin: `-${navHeight + 1}px 0px -60% 0px`,
        threshold: 0,
      }
    );

    this.sections.forEach(section => this.observer.observe(section));
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = null;
    this.intersectingIds?.clear();
    this.pendingActiveId = null;
  }

  activateLink(event) {
    const clickedLink = event.currentTarget;
    this.pendingActiveId = clickedLink.getAttribute('href').slice(1);
    this.#setActive(this.pendingActiveId);
  }

  updateActiveLink(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.intersectingIds.add(entry.target.id);
      } else {
        this.intersectingIds.delete(entry.target.id);
      }
    });

    if (this.pendingActiveId) {
      if (this.intersectingIds.has(this.pendingActiveId)) {
        this.#setActive(this.pendingActiveId);
        return;
      }
      this.pendingActiveId = null;
    }

    const topSection = this.sections.find(section =>
      this.intersectingIds.has(section.id)
    );
    if (!topSection) return;

    this.#setActive(topSection.id);
  }

  #setActive(sectionId) {
    this.links.forEach(link => {
      const isActive = link.getAttribute('href') === `#${sectionId}`;
      link.classList.toggle('active', isActive);
    });
  }
}
